// ─────────────────────────────────────────────────────────────────────────────
// Shared typography config for brand settings.
// Used by BrandEditor (admin preview), update-brand (whitelist) AND the public
// site (layout.js / CSS route), so editor preview and live site match exactly.
//
// Storage is flat on `brand` (backwards compatible with existing data).
// For every role below the keys are:
//
//   family  font<Role>   (Body uses `fontFamily`)
//   weight  fw<Role>      size   text<Role>     style  fs<Role>
//   transform tt<Role>    letter-spacing ls<Role> (legacy letterSpacing<Role> for
//                         Body/Heading/Nav/Label/Button)   line-height lh<Role>
//
// A missing value means "default", resolved in this order:
//   1. the role's own built-in default (ROLE_DEFAULTS)
//   2. its parent role (family / style / transform always; spacing + line
//      height only for children of "All headings")
//   3. GLOBAL_DEFAULTS
//
// Parents:  H1–H6, Display, Lead, Stat → All headings → Body
//           Eyebrow, Caption, Nav, Label, Button → Body
// ─────────────────────────────────────────────────────────────────────────────

const W = (from, to) => { const a = []; for (let w = from; w <= to; w += 100) a.push(w); return a }
// italic: true = italic in every listed weight, false = none, [..] = only those weights
const f = (group, fallback, weights, italic = true) => ({ group, fallback, weights, italic })
// System fonts: never downloaded, rendered with their own fallback stack
const sys = (stack, fallback) => ({ group: SYSTEM, fallback, weights: [400, 700], italic: true, system: true, stack })

const SANS = 'Sans serif'
const SERIF = 'Serif'
const DISPLAY = 'Display'
const MULTI = 'Thai & multi-script'
const SYSTEM = 'System fonts (no download)'

export const FONTS = {
  // ── Sans serif ──────────────────────────────────────────────────────────────
  'Inter':             f(SANS, 'sans-serif', W(300, 900)),
  'Roboto':            f(SANS, 'sans-serif', W(300, 800)),
  'Open Sans':         f(SANS, 'sans-serif', W(300, 800)),
  'Lato':              f(SANS, 'sans-serif', [300, 400, 700, 900]),
  'Montserrat':        f(SANS, 'sans-serif', W(300, 900)),
  'Poppins':           f(SANS, 'sans-serif', W(300, 900)),
  'Raleway':           f(SANS, 'sans-serif', W(300, 900)),
  'Nunito':            f(SANS, 'sans-serif', W(300, 900)),
  'Source Sans 3':     f(SANS, 'sans-serif', [300, 400, 500, 600, 700, 900]),
  'Josefin Sans':      f(SANS, 'sans-serif', W(300, 700)),
  'Work Sans':         f(SANS, 'sans-serif', W(300, 900)),
  'Mulish':            f(SANS, 'sans-serif', W(300, 900)),
  'Rubik':             f(SANS, 'sans-serif', W(300, 900)),
  'DM Sans':           f(SANS, 'sans-serif', W(300, 900)),
  'Outfit':            f(SANS, 'sans-serif', W(300, 900), false),
  'Plus Jakarta Sans': f(SANS, 'sans-serif', W(300, 800)),
  'Space Grotesk':     f(SANS, 'sans-serif', W(300, 700), false),
  'Manrope':           f(SANS, 'sans-serif', W(300, 800), false),
  'Figtree':           f(SANS, 'sans-serif', W(300, 900)),
  'Albert Sans':       f(SANS, 'sans-serif', W(300, 900)),
  'Lexend':            f(SANS, 'sans-serif', W(300, 900), false),
  'Jost':              f(SANS, 'sans-serif', W(300, 900)),
  'Urbanist':          f(SANS, 'sans-serif', W(300, 900)),
  'Be Vietnam Pro':    f(SANS, 'sans-serif', W(300, 900)),
  'Noto Sans':         f(SANS, 'sans-serif', W(300, 900)),
  'IBM Plex Sans':     f(SANS, 'sans-serif', W(300, 700)),
  'Cabin':             f(SANS, 'sans-serif', W(400, 700)),
  'Karla':             f(SANS, 'sans-serif', W(300, 800)),
  'Oxygen':            f(SANS, 'sans-serif', [300, 400, 700], false),
  'Barlow':            f(SANS, 'sans-serif', W(300, 900)),
  'Archivo':           f(SANS, 'sans-serif', W(300, 900)),
  'Libre Franklin':    f(SANS, 'sans-serif', W(300, 900)),
  'Red Hat Display':   f(SANS, 'sans-serif', W(300, 900)),
  'Sora':              f(SANS, 'sans-serif', W(300, 800), false),
  'Epilogue':          f(SANS, 'sans-serif', W(300, 900)),
  'Arimo':             f(SANS, 'sans-serif', W(400, 700)),           // Arial-compatible web font

  // ── Serif ───────────────────────────────────────────────────────────────────
  'Playfair Display':  f(SERIF, 'serif', W(400, 900)),
  'Merriweather':      f(SERIF, 'serif', [300, 400, 700, 900]),
  'Lora':              f(SERIF, 'serif', W(400, 700)),
  'Cormorant Garamond':f(SERIF, 'serif', W(300, 700)),
  'Cormorant':         f(SERIF, 'serif', W(300, 700)),
  'EB Garamond':       f(SERIF, 'serif', W(400, 800)),
  'Crimson Pro':       f(SERIF, 'serif', W(300, 900)),
  'DM Serif Display':  f(SERIF, 'serif', [400]),
  'Fraunces':          f(SERIF, 'serif', W(300, 900)),
  'Libre Baskerville': f(SERIF, 'serif', [400, 700], [400]),
  'PT Serif':          f(SERIF, 'serif', [400, 700]),
  'Bitter':            f(SERIF, 'serif', W(300, 900)),
  'Noto Serif':        f(SERIF, 'serif', W(300, 900)),
  'IBM Plex Serif':    f(SERIF, 'serif', W(300, 700)),
  'Quattrocento':      f(SERIF, 'serif', [400, 700], false),
  'Spectral':          f(SERIF, 'serif', W(300, 800)),
  'Domine':            f(SERIF, 'serif', W(400, 700), false),
  'Zilla Slab':        f(SERIF, 'serif', W(300, 700)),
  'Bodoni Moda':       f(SERIF, 'serif', W(400, 900)),
  'Instrument Serif':  f(SERIF, 'serif', [400]),
  'Unna':              f(SERIF, 'serif', [400, 700]),
  'Prata':             f(SERIF, 'serif', [400], false),
  'Gilda Display':     f(SERIF, 'serif', [400], false),
  'Gelasio':           f(SERIF, 'serif', W(400, 700)),               // Georgia-compatible web font
  'Tinos':             f(SERIF, 'serif', [400, 700]),                // Times-compatible web font

  // ── Display (best for headings only) ────────────────────────────────────────
  'Syne':              f(DISPLAY, 'sans-serif', W(400, 800), false),
  'Cinzel':            f(DISPLAY, 'serif', W(400, 900), false),
  'Marcellus':         f(DISPLAY, 'serif', [400], false),
  'Italiana':          f(DISPLAY, 'serif', [400], false),
  'Tenor Sans':        f(DISPLAY, 'sans-serif', [400], false),
  'Oswald':            f(DISPLAY, 'sans-serif', W(300, 700), false),
  'Bebas Neue':        f(DISPLAY, 'sans-serif', [400], false),
  'Abril Fatface':     f(DISPLAY, 'serif', [400], false),

  // ── Thai & multi-script ─────────────────────────────────────────────────────
  'Sarabun':           f(MULTI, 'sans-serif', W(300, 800)),
  'Prompt':            f(MULTI, 'sans-serif', W(300, 900)),
  'Kanit':             f(MULTI, 'sans-serif', W(300, 900)),
  'Bai Jamjuree':      f(MULTI, 'sans-serif', W(300, 700)),
  'Chakra Petch':      f(MULTI, 'sans-serif', W(300, 700)),
  'Mitr':              f(MULTI, 'sans-serif', W(300, 700), false),
  'Anuphan':           f(MULTI, 'sans-serif', W(300, 700), false),
  'IBM Plex Sans Thai':f(MULTI, 'sans-serif', W(300, 700), false),
  'Noto Sans Thai':    f(MULTI, 'sans-serif', W(300, 900), false),
  'Noto Serif Thai':   f(MULTI, 'serif',      W(300, 900), false),
  'Noto Sans SC':      f(MULTI, 'sans-serif', W(300, 900), false),

  // ── System (already on the visitor's device — nothing to load) ─────────────
  'Georgia':           sys(`Georgia, 'Times New Roman', serif`, 'serif'),
  'Times New Roman':   sys(`'Times New Roman', Times, serif`, 'serif'),
  'Arial':             sys(`Arial, Helvetica, sans-serif`, 'sans-serif'),
  'Helvetica':         sys(`'Helvetica Neue', Helvetica, Arial, sans-serif`, 'sans-serif'),
  'Verdana':           sys(`Verdana, Geneva, sans-serif`, 'sans-serif'),
  'System UI':         sys(`system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif`, 'sans-serif'),
}

const UNKNOWN_FONT = { group: 'Custom', fallback: 'sans-serif', weights: [400, 700], italic: false }

export const fontMeta = (name) => FONTS[name] ?? UNKNOWN_FONT
export const fontStack = (name) => fontMeta(name).stack ?? `'${name}', ${fontMeta(name).fallback}`

export function italicWeights(name) {
  const m = fontMeta(name)
  return m.italic === true ? m.weights : Array.isArray(m.italic) ? m.italic : []
}

export function snapWeight(name, desired) {
  const available = fontMeta(name).weights
  const d = Number(desired) || 400
  return String(available.reduce((a, b) => (Math.abs(b - d) < Math.abs(a - d) ? b : a)))
}

// Grouped data for a Mantine v7 <Select>
export function fontSelectData() {
  const groups = {}
  for (const [name, m] of Object.entries(FONTS)) (groups[m.group] ??= []).push({ value: name, label: name })
  return Object.entries(groups).map(([group, items]) => ({
    group,
    items: items.sort((a, b) => a.label.localeCompare(b.label)),
  }))
}

// ── Roles ─────────────────────────────────────────────────────────────────────
// `cls` is the class to put on any element to give it that text style.
export const ROLES = [
  { id: 'Body',    label: 'Body',          group: 'Base',      cls: 'type-body',    hint: 'Default text everywhere (applied to <body>).' },
  { id: 'Heading', label: 'All headings',  group: 'Base',      cls: null,           hint: 'Shared default for H1–H6, Display, Lead and Stat.' },
  { id: 'H1', label: 'H1', group: 'Headings', cls: 'type-h1', hint: 'Every <h1>, or any element with class type-h1.' },
  { id: 'H2', label: 'H2', group: 'Headings', cls: 'type-h2', hint: 'Every <h2>, or class type-h2. Section titles.' },
  { id: 'H3', label: 'H3', group: 'Headings', cls: 'type-h3', hint: 'Every <h3>, or class type-h3. Card / form titles.' },
  { id: 'H4', label: 'H4', group: 'Headings', cls: 'type-h4', hint: 'Every <h4>, or class type-h4.' },
  { id: 'H5', label: 'H5', group: 'Headings', cls: 'type-h5', hint: 'Every <h5>, or class type-h5.' },
  { id: 'H6', label: 'H6', group: 'Headings', cls: 'type-h6', hint: 'Every <h6>, or class type-h6.' },
  { id: 'Display', label: 'Display',       group: 'Display text', cls: 'type-display', hint: 'Oversized hero / banner titles.' },
  { id: 'Lead',    label: 'Lead / Tagline', group: 'Display text', cls: 'type-lead',   hint: 'Taglines and intro sentences under a heading.' },
  { id: 'Eyebrow', label: 'Eyebrow',       group: 'Display text', cls: 'type-eyebrow', hint: 'Small kicker text above a heading.' },
  { id: 'Stat',    label: 'Stat / Number', group: 'Display text', cls: 'type-stat',    hint: 'Big numbers and prices.' },
  { id: 'Caption', label: 'Caption',       group: 'Display text', cls: 'type-caption', hint: 'Meta text: locations, spec labels, notes under images.' },
  { id: 'Nav',     label: 'Navigation',    group: 'Interface', cls: 'type-nav',    hint: 'Menu links. Add class type-nav to navbar links.' },
  { id: 'Label',   label: 'Label / Badge', group: 'Interface', cls: 'type-label',  hint: 'Badges and chips, e.g. "FOR SALE". Class type-label.' },
  { id: 'Button',  label: 'Button',        group: 'Interface', cls: 'type-button', hint: 'Button text. Class type-button.' },
]

export const ROLE_GROUPS = [...new Set(ROLES.map(r => r.group))]
export const roleInfo = (id) => ROLES.find(r => r.id === id)

// Roles that map to real elements (Heading is only a shared default)
export const RENDERED_ROLES = ROLES.map(r => r.id).filter(id => id !== 'Heading')

export const ROLE_PARENT = {
  Body: null, Heading: 'Body',
  H1: 'Heading', H2: 'Heading', H3: 'Heading', H4: 'Heading', H5: 'Heading', H6: 'Heading',
  Display: 'Heading', Lead: 'Heading', Stat: 'Heading',
  Eyebrow: 'Body', Caption: 'Body',
  Nav: 'Body', Label: 'Body', Button: 'Body',
}

// true if `id` takes defaults from `ancestor` (directly or through a parent)
export function descendsFrom(id, ancestor) {
  for (let p = ROLE_PARENT[id]; p; p = ROLE_PARENT[p]) if (p === ancestor) return true
  return false
}

// For selects in element editors (property page, home sections, …):
//   <Select data={TEXT_STYLE_OPTIONS} …/>  →  <p className={typeClass(cfg.textStyle)}>
export const TEXT_STYLE_OPTIONS = [
  { value: '', label: 'Element default' },
  ...ROLES.filter(r => r.cls).map(r => ({ value: r.id.toLowerCase(), label: r.label })),
]
export const typeClass = (role) => (role ? `type-${String(role).toLowerCase()}` : '')

// Inline-style equivalent of a type-* class (uses the CSS variables, so it
// follows the live brand settings). For components that can't take a class.
export function typeStyle(role) {
  if (!role) return {}
  const n = String(role).toLowerCase()
  const fam = n === 'body' ? '--font-body' : `--font-${n}`
  return {
    fontFamily:    `var(${fam})`,
    fontWeight:    `var(--font-${n}-weight)`,
    fontSize:      `var(--font-${n}-size)`,
    fontStyle:     `var(--font-${n}-style)`,
    textTransform: `var(--font-${n}-transform)`,
    letterSpacing: `var(--font-${n}-ls)`,
    lineHeight:    `var(--font-${n}-lh)`,
  }
}

// Neutral built-in defaults. Existing sites rely on these for any key they
// never saved, so don't restyle them here — ship a look as a preset instead.
const GLOBAL_DEFAULTS = {
  family: 'Playfair Display', weight: '400', size: '1rem', style: 'normal',
  transform: 'none', letterSpacing: 'normal', lineHeight: '1.4',
}

const ROLE_DEFAULTS = {
  Body:    { size: '1rem',     weight: '400', lineHeight: '1.6' },
  Heading: { lineHeight: '1.2' },
  H1:      { size: '2.25rem',  weight: '800' },
  H2:      { size: '1.875rem', weight: '700' },
  H3:      { size: '1.5rem',   weight: '700' },
  H4:      { size: '1.25rem',  weight: '600' },
  H5:      { size: '1.125rem', weight: '600' },
  H6:      { size: '1rem',     weight: '600' },
  Display: { size: 'clamp(2.75rem, 6vw, 4.5rem)', weight: '700', lineHeight: '1' },
  Lead:    { size: '1.25rem',  weight: '400', lineHeight: '1.5' },
  Stat:    { size: '1.75rem',  weight: '700', lineHeight: '1.1' },
  Eyebrow: { size: '0.75rem',  weight: '600', transform: 'uppercase', letterSpacing: '0.15em', lineHeight: '1.6' },
  Caption: { size: '0.8125rem', weight: '400', lineHeight: '1.5' },
  Nav:     { size: '0.9375rem', weight: '500' },
  Label:   { size: '0.75rem',  weight: '600' },
  Button:  { size: '0.875rem', weight: '600' },
}

const LEGACY_LS = {
  Body: 'letterSpacingBody', Heading: 'letterSpacingHeading', Nav: 'letterSpacingNav',
  Label: 'letterSpacingLabel', Button: 'letterSpacingButton',
}

export const TYPO_PROPS = ['family', 'weight', 'size', 'style', 'transform', 'letterSpacing', 'lineHeight']

// Which brand key stores a given property of a role
export function keyFor(id, prop) {
  switch (prop) {
    case 'family':        return id === 'Body' ? 'fontFamily' : `font${id}`
    case 'weight':        return `fw${id}`
    case 'size':          return `text${id}`
    case 'style':         return `fs${id}`
    case 'transform':     return `tt${id}`
    case 'letterSpacing': return LEGACY_LS[id] ?? `ls${id}`
    case 'lineHeight':    return `lh${id}`
    default: throw new Error(`Unknown typography prop: ${prop}`)
  }
}

// Every brand key the typography system can write (used by the save whitelist)
export const TYPOGRAPHY_KEYS = ROLES.flatMap(r => TYPO_PROPS.map(p => keyFor(r.id, p)))

// family / style / transform cascade for every role;
// letter-spacing / line-height only cascade from "All headings" to its children
export const inheritsProp = (id, prop) =>
  ['family', 'style', 'transform'].includes(prop) || ROLE_PARENT[id] === 'Heading'

const isSet = (v) => v !== undefined && v !== null && v !== ''

export function resolveProp(brand = {}, id, prop, { skipOwn = false } = {}) {
  if (!skipOwn) {
    const v = brand[keyFor(id, prop)]
    if (isSet(v)) return String(v)
  }
  const d = ROLE_DEFAULTS[id]?.[prop]
  if (d !== undefined) return d
  const parent = ROLE_PARENT[id]
  if (parent && inheritsProp(id, prop)) return resolveProp(brand, parent, prop)
  return GLOBAL_DEFAULTS[prop]
}

// What a role would get if its own value were cleared
export const resolveDefault = (brand, id, prop) => resolveProp(brand, id, prop, { skipOwn: true })

export function resolveRole(brand = {}, id) {
  const family = resolveProp(brand, id, 'family')
  return {
    family,
    weight:        snapWeight(family, resolveProp(brand, id, 'weight')),
    size:          resolveProp(brand, id, 'size'),
    style:         resolveProp(brand, id, 'style'),
    transform:     resolveProp(brand, id, 'transform'),
    letterSpacing: resolveProp(brand, id, 'letterSpacing'),
    lineHeight:    resolveProp(brand, id, 'lineHeight'),
  }
}

// True when a role has any value of its own (Body always has a font, so skip it)
export function hasOverride(brand = {}, id) {
  if (id === 'Body') return false
  return TYPO_PROPS.some(p => isSet(brand[keyFor(id, p)]))
}

// ── Font loading ──────────────────────────────────────────────────────────────
// One URL per family: if Google rejects one request, the other fonts still load.
// System fonts are skipped — they are already on the device.
export function fontImportUrls(brand = {}) {
  const usage = new Map()
  const add = (family, weight, italic) => {
    if (fontMeta(family).system) return
    if (!usage.has(family)) usage.set(family, { roman: new Set(), italic: new Set() })
    const u = usage.get(family)
    const w = Number(snapWeight(family, weight))
    // No real italic available → load roman; the browser will slant it
    if (italic && italicWeights(family).includes(w)) u.italic.add(w)
    else u.roman.add(w)
  }

  for (const id of RENDERED_ROLES) {
    const r = resolveRole(brand, id)
    add(r.family, r.weight, r.style === 'italic')
  }
  // Body copy also needs <strong> and <em>
  const body = resolveRole(brand, 'Body')
  add(body.family, 700, false)
  add(body.family, body.weight, true)

  return [...usage.entries()].map(([family, { roman, italic }]) => {
    const fam = family.replace(/ /g, '+')
    const r = [...roman].sort((a, b) => a - b)
    const i = [...italic].sort((a, b) => a - b)
    const axis = i.length
      ? `ital,wght@${[...r.map(w => `0,${w}`), ...i.map(w => `1,${w}`)].join(';')}`
      : `wght@${r.join(';')}`
    return `https://fonts.googleapis.com/css2?family=${fam}:${axis}&display=swap`
  })
}

// ── CSS output for the live site ──────────────────────────────────────────────
const clean = (v) => String(v).replace(/[;{}<>]/g, '').trim()

// `html h1` (0,0,2) beats Tailwind preflight's `h1 { font-size: inherit }` (0,0,1).
// A type-* class (0,1,0) beats both, so <h2 class="type-display"> gets Display.
// Tailwind utilities like `text-4xl` still win over the element rules.
function selectorFor(id) {
  const n = id.toLowerCase()
  if (id === 'Body') return 'html body, .type-body'
  if (/^H[1-6]$/.test(id)) return `html ${n}, .type-${n}`
  return `.type-${n}`
}

/**
 * Returns:
 *  - urls:    Google Fonts stylesheet URLs (render as <link> tags — preferred)
 *  - imports: the same as @import lines, if you'd rather inline them in a CSS route
 *  - css:     CSS variables + rules for body, h1–h6 and every .type-* class
 */
export function buildTypographyCss(brand = {}) {
  const urls = fontImportUrls(brand)
  const vars = [
    `--font-body: ${fontStack(resolveProp(brand, 'Body', 'family'))};`,
    `--font-heading: ${fontStack(resolveProp(brand, 'Heading', 'family'))};`,
  ]
  const rules = []

  for (const id of RENDERED_ROLES) {
    const r = resolveRole(brand, id)
    const n = id.toLowerCase()
    const fam = id === 'Body' ? '--font-body' : `--font-${n}`
    if (id !== 'Body') vars.push(`${fam}: ${fontStack(r.family)};`)
    vars.push(
      `--font-${n}-weight: ${clean(r.weight)};`,
      `--font-${n}-size: ${clean(r.size)};`,
      `--font-${n}-style: ${clean(r.style)};`,
      `--font-${n}-transform: ${clean(r.transform)};`,
      `--font-${n}-ls: ${clean(r.letterSpacing)};`,
      `--font-${n}-lh: ${clean(r.lineHeight)};`,
    )
    rules.push(
      `${selectorFor(id)} {` +
      ` font-family: var(${fam});` +
      ` font-weight: var(--font-${n}-weight);` +
      ` font-size: var(--font-${n}-size);` +
      ` font-style: var(--font-${n}-style);` +
      ` text-transform: var(--font-${n}-transform);` +
      ` letter-spacing: var(--font-${n}-ls);` +
      ` line-height: var(--font-${n}-lh); }`
    )
  }

  return {
    urls,
    imports: urls.map(u => `@import url('${u}');`).join('\n'),
    css: `:root {\n  ${vars.join('\n  ')}\n}\n${rules.join('\n')}\n`,
  }
}